import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import usePersistentState from '../utils/usePersistentState';
import toast from 'react-hot-toast';
import { settingsAPI, requirementAPI, bustCache } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Button, Spinner, SearchableSelect } from '../components/UI';
import RichTextEditor from '../components/RichTextEditor';
import SafeHtml from '../components/SafeHtml';

// ─── Settings cards config ────────────────────────────────────────────────────
// To add a new section, push a new entry here and add a matching modal below.
const SETTING_CARDS = [
  {
    id: 'candidate',
    label: 'Candidate Configuration',
    icon: '👤',
    description: 'Submission checklist & form requirements for candidate profiles.',
    color: { bg: 'rgba(99,102,241,0.08)', border: 'rgba(99,102,241,0.25)', accent: '#6366f1', accentDark: '#818cf8' },
  },
  {
    id: 'email',
    label: 'Email Configuration',
    icon: '✉️',
    description: 'Globally enable or disable email channels for candidates and clients.',
    color: { bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.25)', accent: '#10b981', accentDark: '#34d399' },
  },
  {
    id: 'impersonation',
    label: 'Impersonation',
    icon: '🎭',
    description: 'Session duration and blocked sensitive actions for SuperAdmin impersonation.',
    color: { bg: 'rgba(180,83,9,0.08)', border: 'rgba(180,83,9,0.25)', accent: '#b45309', accentDark: '#fbbf24' },
  },
  {
    id: 'cache',
    label: 'Performance / Cache',
    icon: '⚡',
    description: 'Turn Redis caching on or off. When off, every request reads directly from the database.',
    color: { bg: 'rgba(6,182,212,0.08)', border: 'rgba(6,182,212,0.25)', accent: '#06b6d4', accentDark: '#22d3ee' },
    superAdminOnly: true,
  },
  {
    id: 'files',
    label: 'Downloads',
    icon: '📄',
    description: 'How long a resume view/download link stays valid before it expires.',
    color: { bg: 'rgba(168,85,247,0.08)', border: 'rgba(168,85,247,0.25)', accent: '#a855f7', accentDark: '#c084fc' },
    superAdminOnly: true,
  },
  {
    id: 'teams',
    label: 'Teams Meetings',
    icon: '📅',
    description: 'Microsoft credentials for auto-creating Teams join links on interview emails.',
    color: { bg: 'rgba(59,130,246,0.08)', border: 'rgba(59,130,246,0.25)', accent: '#3b82f6', accentDark: '#93c5fd' },
    superAdminOnly: true,
  },
  {
    id: 'db-backups',
    label: 'Database Backups',
    icon: '🗄️',
    description: 'Daily automatic backups to S3, on-demand backup, and one-click restore from a backup.',
    color: { bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.25)', accent: '#ef4444', accentDark: '#f87171' },
    superAdminOnly: true,
  },
];

// ─── Small helpers ────────────────────────────────────────────────────────────
function IconBtn({ onClick, title, children, color = 'var(--text-muted)' }) {
  return (
    <button type="button" onClick={onClick} title={title}
      style={{ background: 'none', border: 'none', cursor: 'pointer', color, padding: '4px 6px', borderRadius: 6, lineHeight: 1, transition: 'color 0.15s' }}>
      {children}
    </button>
  );
}

// ─── Generic popup modal ─────────────────────────────────────────────────────
function SettingsModal({ isOpen, onClose, title, icon, children }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      className="p-3 sm:p-5"
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div style={{
        width: '100%', maxWidth: 620, maxHeight: '92vh',
        background: isDark ? 'var(--bg-card)' : '#ffffff',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        borderRadius: 16,
        boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Modal header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'}`,
          flexShrink: 0,
        }}>
          <div className="flex items-center gap-3">
            <span style={{ fontSize: 22 }}>{icon}</span>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
              {title}
            </h2>
          </div>
          <button onClick={onClose}
            style={{
              background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
              border: 'none', borderRadius: 8, width: 32, height: 32,
              cursor: 'pointer', color: 'var(--text-muted)', fontSize: 16,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.15s',
            }}>
            ✕
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ─── Shared checklist editor — rich-text items with add / edit / reorder ──────
function ChecklistEditor({ items, onChange, isDark, canEdit, addPlaceholder }) {
  const [draft, setDraft]         = useState('');
  const [editingId, setEditingId] = useState(null);

  // Strip tags so we can tell a truly-empty editor (Quill renders <p><br></p>).
  const plain = (html) => String(html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

  const commit = () => {
    if (!plain(draft)) return;
    if (editingId) onChange(items.map(i => i.id === editingId ? { ...i, label: draft } : i));
    else           onChange([...items, { id: `item_${Date.now()}`, label: draft }]);
    setDraft(''); setEditingId(null);
  };
  const startEdit  = (item) => { setEditingId(item.id); setDraft(item.label || ''); };
  const cancelEdit = () => { setEditingId(null); setDraft(''); };
  const remove     = (id) => { onChange(items.filter(i => i.id !== id)); if (editingId === id) cancelEdit(); };
  const move = (index, dir) => {
    const next = [...items]; const swap = index + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[index], next[swap]] = [next[swap], next[index]];
    onChange(next);
  };

  const accent = isDark ? '#a5b4fc' : '#4338ca';
  const accentBg = isDark ? 'rgba(99,102,241,0.20)' : '#eef2ff';
  const accentBorder = isDark ? 'rgba(99,102,241,0.40)' : '#c7d2fe';

  return (
    <div>
      {items.length === 0 ? (
        <div style={{
          border: `1px dashed ${isDark ? 'rgba(255,255,255,0.14)' : '#cbd5e1'}`,
          borderRadius: 12, padding: 28, textAlign: 'center',
          background: isDark ? 'rgba(255,255,255,0.02)' : '#f8fafc',
        }}>
          <p style={{ fontSize: 26, marginBottom: 6 }}>📋</p>
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No checklist items yet — add your first one below.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item, index) => {
            const isEditing = editingId === item.id;
            return (
              <div key={item.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: isEditing ? accentBg : 'var(--bg-raised)',
                border: `1px solid ${isEditing ? accentBorder : 'var(--border-subtle)'}`,
                borderRadius: 10, padding: '9px 12px',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
              }}
                onMouseEnter={e => { if (!isEditing) { e.currentTarget.style.borderColor = accentBorder; e.currentTarget.style.boxShadow = isDark ? '0 2px 10px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.06)'; } }}
                onMouseLeave={e => { if (!isEditing) { e.currentTarget.style.borderColor = 'var(--border-subtle)'; e.currentTarget.style.boxShadow = 'none'; } }}
              >
                {canEdit && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
                    <IconBtn onClick={() => move(index, -1)} title="Move up" color={index === 0 ? 'transparent' : 'var(--text-muted)'}>▲</IconBtn>
                    <IconBtn onClick={() => move(index, 1)} title="Move down" color={index === items.length - 1 ? 'transparent' : 'var(--text-muted)'}>▼</IconBtn>
                  </div>
                )}
                <span style={{
                  width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, background: accentBg, color: accent,
                }}>{index + 1}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <SafeHtml html={item.label} style={{ fontSize: 13.5, color: 'var(--text-primary)', lineHeight: 1.5 }} />
                </div>
                {canEdit && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconBtn onClick={() => startEdit(item)} title="Edit" color={isEditing ? accent : 'var(--text-muted)'}>✎</IconBtn>
                    <IconBtn onClick={() => remove(item.id)} title="Remove" color={isDark ? '#f87171' : '#ef4444'}>✕</IconBtn>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {canEdit && (
        <div style={{
          marginTop: 14, padding: 14, borderRadius: 12,
          background: editingId ? (isDark ? 'rgba(99,102,241,0.07)' : '#f5f7ff') : (isDark ? 'rgba(255,255,255,0.02)' : '#f8fafc'),
          border: `1px solid ${editingId ? accentBorder : 'var(--border-subtle)'}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 9 }}>
            <span style={{ fontSize: 13 }}>{editingId ? '✎' : '➕'}</span>
            <p style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)', margin: 0, letterSpacing: 0.2 }}>
              {editingId ? 'EDIT CHECKLIST ITEM' : 'NEW CHECKLIST ITEM'}
            </p>
          </div>
          <RichTextEditor
            value={draft}
            onChange={setDraft}
            rows={2}
            placeholder={addPlaceholder || 'Type the checklist item…'}
          />
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={commit} disabled={!plain(draft)}>
              {editingId ? 'Update item' : '+ Add item'}
            </Button>
            {editingId && <Button size="sm" variant="secondary" onClick={cancelEdit}>Cancel</Button>}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Per-requirement checklist editor (within Candidate Configuration) ────────
function PerRequirementChecklist({ isDark }) {
  const { isSuperAdmin } = useAuth();
  const [requirements, setRequirements] = useState([]);
  const [reqId, setReqId]     = useState('');
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    requirementAPI.dropdown()
      .then(({ data }) => setRequirements(data.requirements || []))
      .catch(() => {});
  }, []);

  const pick = async (rid) => {
    setReqId(rid); setItems([]);
    if (!rid) return;
    setLoading(true);
    try { const { data } = await settingsAPI.getRequirementChecklist(rid); setItems(data.items || []); }
    catch { setItems([]); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!reqId) return;
    setSaving(true);
    try { await settingsAPI.updateRequirementChecklist(reqId, items); toast.success('Requirement checklist saved!'); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 16 }}>
        Extra items required only for a specific requirement. When that requirement is selected on the
        candidate form, these appear <strong>in addition to</strong> the global checklist.
      </p>

      <div style={{ maxWidth: 380, width: '100%', marginBottom: 14 }}>
        <SearchableSelect
          label="Requirement"
          value={reqId}
          onChange={pick}
          placeholder="— Select a requirement —"
          options={requirements.map(r => ({ value: r.id, label: r.jobTitle, sub: r.client?.clientName || '' }))}
        />
      </div>

      {reqId && (loading ? (
        <div className="flex justify-center py-6"><Spinner /></div>
      ) : (
        <>
          <ChecklistEditor
            items={items}
            onChange={setItems}
            isDark={isDark}
            canEdit={isSuperAdmin()}
            addPlaceholder="e.g. Client-specific NDA signed"
          />
          {isSuperAdmin() && (
            <div className="flex items-center justify-between mt-3">
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{items.length} item{items.length !== 1 ? 's' : ''} for this requirement</p>
              <Button onClick={handleSave} loading={saving} size="sm">Save Requirement Checklist</Button>
            </div>
          )}
        </>
      ))}
    </div>
  );
}

// ─── Candidate Configuration modal content ────────────────────────────────────
function CandidateConfigContent({ isDark, onClose }) {
  const { isSuperAdmin } = useAuth();
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  // Kept for this tab — survives leaving the page and coming back.
  const [tab, setTab]           = usePersistentState('settings:tab', 'global');

  useEffect(() => {
    settingsAPI.getChecklist()
      .then(({ data }) => setItems(data.items || []))
      .catch(() => toast.error('Failed to load checklist'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (items.some(i => !i.label.trim())) {
      toast.error('All items must have a label.');
      return;
    }
    setSaving(true);
    try {
      await settingsAPI.updateChecklist(items);
      toast.success('Checklist saved!');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save checklist.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-10"><Spinner /></div>;

  const TABS = [
    { id: 'global', label: 'Global Checklist',   icon: '✅' },
    { id: 'req',    label: 'Per-Requirement',     icon: '📌' },
  ];

  return (
    <div className="space-y-5">
      {/* Tab switcher */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 4, padding: 4, borderRadius: 12,
        background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)',
        width: 'fit-content', maxWidth: '100%',
      }}>
        {TABS.map(t => {
          const active = tab === t.id;
          return (
            <button key={t.id} type="button" onClick={() => setTab(t.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 9, fontSize: 13, fontWeight: 600,
                border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                background: active ? (isDark ? 'rgba(99,102,241,0.25)' : '#ffffff') : 'transparent',
                color: active ? (isDark ? '#a5b4fc' : '#4338ca') : 'var(--text-secondary)',
                boxShadow: active ? (isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.08)') : 'none',
              }}>
              <span>{t.icon}</span> {t.label}
            </button>
          );
        })}
      </div>

      {/* ── Global tab ─────────────────────────────────────────────────────── */}
      {tab === 'global' && (
        <div>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 16 }}>
            Items recruiters must confirm before saving <strong>any</strong> new candidate. All are mandatory —
            the form blocks submission until every box is ticked, and the list is locked onto that candidate once saved.
          </p>

          <ChecklistEditor
            items={items}
            onChange={setItems}
            isDark={isDark}
            canEdit={isSuperAdmin()}
            addPlaceholder="e.g. Resume reviewed and verified"
          />

          <div className="flex items-center justify-between pt-4 mt-5"
            style={{ borderTop: '1px solid var(--border-subtle)' }}>
            <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {items.length} global item{items.length !== 1 ? 's' : ''} configured
            </p>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={onClose}>{isSuperAdmin() ? 'Cancel' : 'Close'}</Button>
              {isSuperAdmin() && <Button onClick={handleSave} loading={saving}>Save Global Checklist</Button>}
            </div>
          </div>
        </div>
      )}

      {/* ── Per-requirement tab ────────────────────────────────────────────── */}
      {tab === 'req' && (
        <div>
          <PerRequirementChecklist isDark={isDark} />
          <div className="flex justify-end pt-4 mt-5" style={{ borderTop: '1px solid var(--border-subtle)' }}>
            <Button variant="secondary" onClick={onClose}>Close</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Email Configuration modal content ───────────────────────────────────────
const EMAIL_SENDER_FIELDS = [
  { key: 'senderCandidate', label: 'Candidate', icon: '👤', hint: 'From: address for emails to candidates.' },
  { key: 'senderClient',    label: 'Client',    icon: '🏢', hint: 'From: address for emails to client contacts.' },
  { key: 'senderSystem',    label: 'System',    icon: '⚙️', hint: 'From: address for system/notification emails.' },
];
const EMPTY_EMAIL_CFG = {
  candidateEmailEnabled: true, clientEmailEnabled: true,
  senderCandidate: '', senderClient: '', senderSystem: '',
};
function EmailConfigContent({ isDark, onClose }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SuperAdmin';

  const [config, setConfig]   = useState(EMPTY_EMAIL_CFG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    settingsAPI.getEmailConfig()
      .then(({ data }) => setConfig({ ...EMPTY_EMAIL_CFG, ...(data.config || {}) }))
      .catch(() => toast.error('Failed to load email config'))
      .finally(() => setLoading(false));
  }, []);

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());

  const handleSave = async () => {
    // Validate the three sender addresses (format only — SES verification is on you).
    for (const f of EMAIL_SENDER_FIELDS) {
      if (!isEmail(config[f.key])) {
        toast.error(`${f.label} sender must be a valid email address.`);
        return;
      }
    }
    setSaving(true);
    try {
      await settingsAPI.updateEmailConfig(config);
      toast.success('Email configuration saved!');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save email configuration.');
    } finally {
      setSaving(false);
    }
  };

  const ToggleRow = ({ label, description, icon, value, onChange, disabled }) => (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      padding: '14px 16px',
      background: 'var(--bg-raised)',
      border: `1px solid var(--border-subtle)`,
      borderRadius: 10,
      opacity: disabled ? 0.6 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 22, lineHeight: 1.2 }}>{icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', margin: 0, overflowWrap: 'anywhere' }}>{label}</p>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3, lineHeight: 1.5, overflowWrap: 'anywhere' }}>{description}</p>
        </div>
      </div>
      {/* Toggle switch */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && onChange(!value)}
        title={disabled ? 'Only SuperAdmin can change this setting' : (value ? 'Click to disable' : 'Click to enable')}
        style={{
          position: 'relative', flexShrink: 0,
          width: 48, height: 26, borderRadius: 13,
          background: value
            ? (isDark ? '#10b981' : '#10b981')
            : (isDark ? 'rgba(255,255,255,0.12)' : '#d1d5db'),
          border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'background 0.2s',
          padding: 0, outline: 'none',
        }}
      >
        <span style={{
          position: 'absolute',
          top: 3, left: value ? 25 : 3,
          width: 20, height: 20, borderRadius: '50%',
          background: '#ffffff',
          boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
          transition: 'left 0.2s',
          display: 'block',
        }} />
      </button>
    </div>
  );

  if (loading) return <div className="flex justify-center py-10"><Spinner /></div>;

  return (
    <div className="space-y-6">
      {/* Info banner */}
      <div style={{
        padding: '12px 14px',
        background: isDark ? 'rgba(16,185,129,0.08)' : 'rgba(16,185,129,0.06)',
        border: `1px solid ${isDark ? 'rgba(16,185,129,0.25)' : 'rgba(16,185,129,0.3)'}`,
        borderRadius: 10,
        display: 'flex', gap: 10, alignItems: 'flex-start',
      }}>
        <span style={{ fontSize: 16, lineHeight: 1.4 }}>ℹ️</span>
        <p style={{ fontSize: 12, color: isDark ? '#6ee7b7' : '#065f46', margin: 0, lineHeight: 1.6 }}>
          {isSuperAdmin
            ? 'These switches control whether emails can be sent through each channel. When a channel is disabled, no user — regardless of role — can send emails through it.'
            : 'These settings are managed by a SuperAdmin. You can see the current state but cannot change them.'}
        </p>
      </div>

      {/* Toggle rows */}
      <div className="space-y-3">
        <ToggleRow
          label="Candidate Emails"
          description="Allow sending emails directly to candidates (interview invites, JD emails, bulk sends, etc.)."
          icon="👤"
          value={config.candidateEmailEnabled}
          onChange={val => setConfig(prev => ({ ...prev, candidateEmailEnabled: val }))}
          disabled={!isSuperAdmin}
        />
        <ToggleRow
          label="Client Emails"
          description="Allow sending emails to client contacts (profiles, interview notifications, etc.)."
          icon="🏢"
          value={config.clientEmailEnabled}
          onChange={val => setConfig(prev => ({ ...prev, clientEmailEnabled: val }))}
          disabled={!isSuperAdmin}
        />
      </div>

      {/* Sender addresses */}
      <div style={{
        padding: '14px 16px', background: 'var(--bg-raised)',
        border: '1px solid var(--border-subtle)', borderRadius: 10,
        opacity: isSuperAdmin ? 1 : 0.6,
      }}>
        <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 4px' }}>Sender addresses (From)</p>
        <div style={{
          display: 'flex', gap: 8, alignItems: 'flex-start',
          padding: '8px 10px', marginBottom: 12, borderRadius: 8,
          background: isDark ? 'rgba(245,158,11,0.10)' : 'rgba(245,158,11,0.08)',
          border: `1px solid ${isDark ? 'rgba(245,158,11,0.3)' : 'rgba(245,158,11,0.35)'}`,
        }}>
          <span style={{ fontSize: 14, lineHeight: 1.4 }}>⚠️</span>
          <p style={{ fontSize: 11.5, color: isDark ? '#fcd34d' : '#92400e', margin: 0, lineHeight: 1.6 }}>
            Each address must be <strong>verified in AWS SES</strong>, or emails from that channel will fail to send.
          </p>
        </div>
        <div className="space-y-3">
          {EMAIL_SENDER_FIELDS.map(f => (
            <div key={f.key}>
              <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>
                <span style={{ marginRight: 6 }}>{f.icon}</span>{f.label}
              </label>
              <input
                type="email" inputMode="email" autoComplete="off" placeholder="name@domain.com"
                value={config[f.key] || ''}
                disabled={!isSuperAdmin || saving}
                onChange={e => setConfig(prev => ({ ...prev, [f.key]: e.target.value }))}
                style={{
                  width: '100%', background: 'var(--bg-input)', border: '1px solid var(--border-input)',
                  borderRadius: 8, padding: '8px 12px', fontSize: 13, color: 'var(--text-primary)', outline: 'none',
                }}
              />
              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '3px 0 0', lineHeight: 1.5, overflowWrap: 'anywhere' }}>{f.hint}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Status summary */}
      <div style={{
        padding: '10px 14px',
        background: 'var(--bg-raised)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 8,
        fontSize: 12, color: 'var(--text-muted)',
        lineHeight: 1.6,
      }}>
        Current status: Candidate emails are <strong style={{ color: config.candidateEmailEnabled ? '#10b981' : '#ef4444' }}>
          {config.candidateEmailEnabled ? 'enabled' : 'disabled'}
        </strong>, Client emails are <strong style={{ color: config.clientEmailEnabled ? '#10b981' : '#ef4444' }}>
          {config.clientEmailEnabled ? 'enabled' : 'disabled'}
        </strong>.
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 pt-4"
        style={{ borderTop: '1px solid var(--border-subtle)' }}>
        <Button variant="secondary" onClick={onClose}>
          {isSuperAdmin ? 'Cancel' : 'Close'}
        </Button>
        {isSuperAdmin && (
          <Button onClick={handleSave} loading={saving}>Save Changes</Button>
        )}
      </div>
    </div>
  );
}

// ─── Impersonation Configuration modal content ────────────────────────────────
function ImpersonationConfigContent({ isDark, onClose }) {
  const { user, refreshAttribution } = useAuth();
  const isSuperAdmin = user?.role === 'SuperAdmin';

  const [config, setConfig]   = useState({ maxDurationMinutes: 30, blockedActions: [], showImpersonatorAttribution: true });
  const [validActions, setValidActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    settingsAPI.getImpersonationConfig()
      .then(({ data }) => {
        setConfig({
          maxDurationMinutes: 30,
          blockedActions: [],
          showImpersonatorAttribution: true,
          ...(data.config || {}),
        });
        setValidActions(data.validBlockedActions || []);
      })
      .catch(() => toast.error('Failed to load impersonation config'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!Number.isInteger(config.maxDurationMinutes) || config.maxDurationMinutes < 1 || config.maxDurationMinutes > 1440) {
      toast.error('Duration must be between 1 and 1440 minutes.');
      return;
    }
    setSaving(true);
    try {
      await settingsAPI.updateImpersonationConfig(config);
      // Refresh the AuthContext's public attribution flag so the
      // "(impersonated by …)" suffix appears/disappears immediately
      // across the app, without waiting for a full reload.
      if (refreshAttribution) await refreshAttribution();
      toast.success('Impersonation configuration saved!');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const toggleAction = (action) => {
    setConfig(prev => {
      const set = new Set(prev.blockedActions || []);
      if (set.has(action)) set.delete(action); else set.add(action);
      return { ...prev, blockedActions: Array.from(set) };
    });
  };

  if (loading) return <div className="flex justify-center py-10"><Spinner /></div>;

  return (
    <div className="space-y-6">
      <div style={{
        padding: '12px 14px',
        background: isDark ? 'rgba(180,83,9,0.10)' : 'rgba(180,83,9,0.06)',
        border: `1px solid ${isDark ? 'rgba(180,83,9,0.3)' : 'rgba(180,83,9,0.3)'}`,
        borderRadius: 10,
        display: 'flex', gap: 10, alignItems: 'flex-start',
      }}>
        <span style={{ fontSize: 16, lineHeight: 1.4 }}>⚠️</span>
        <p style={{ fontSize: 12, color: isDark ? '#fbbf24' : '#92400e', margin: 0, lineHeight: 1.6 }}>
          Impersonation lets a SuperAdmin act as any user. These limits apply to every session — the actor
          is automatically returned to their own account when the duration elapses, and any "blocked"
          actions return 403 even mid-session.
        </p>
      </div>

      {/* Attribution toggle */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        padding: '14px 16px',
        background: 'var(--bg-raised)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 10,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', margin: 0, overflowWrap: 'anywhere' }}>
            Show impersonator in attributions
          </p>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.5, overflowWrap: 'anywhere' }}>
            When on, records created during impersonation display the actor's name in parentheses
            (e.g. "Recruited by Akash (impersonated by Vishnu)"). Off hides the suffix everywhere.
          </p>
        </div>
        <button
          type="button"
          disabled={!isSuperAdmin}
          onClick={() => setConfig(p => ({ ...p, showImpersonatorAttribution: !p.showImpersonatorAttribution }))}
          title={isSuperAdmin ? (config.showImpersonatorAttribution ? 'Click to disable' : 'Click to enable') : 'SuperAdmin only'}
          style={{
            position: 'relative', flexShrink: 0,
            width: 48, height: 26, borderRadius: 13,
            background: config.showImpersonatorAttribution ? '#10b981' : (isDark ? 'rgba(255,255,255,0.12)' : '#d1d5db'),
            border: 'none', cursor: isSuperAdmin ? 'pointer' : 'not-allowed',
            transition: 'background 0.2s',
            padding: 0,
          }}
        >
          <span style={{
            position: 'absolute', top: 3,
            left: config.showImpersonatorAttribution ? 25 : 3,
            width: 20, height: 20, borderRadius: '50%',
            background: '#ffffff', boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
            transition: 'left 0.2s', display: 'block',
          }} />
        </button>
      </div>

      {/* Duration */}
      <div>
        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', display: 'block', marginBottom: 6 }}>
          Maximum session duration (minutes)
        </label>
        <input
          type="number" min={1} max={1440}
          disabled={!isSuperAdmin}
          value={config.maxDurationMinutes}
          onChange={e => setConfig(prev => ({ ...prev, maxDurationMinutes: Number(e.target.value) }))}
          style={{
            width: '100%',
            background: 'var(--bg-input)',
            border: '1px solid var(--border-input)',
            borderRadius: 8,
            padding: '8px 12px',
            fontSize: 13,
            color: 'var(--text-primary)',
            outline: 'none',
          }}
        />
        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          Range: 1–1440 minutes (24 hours). Default: 30. Shorter is safer.
        </p>
      </div>

      {/* Blocked actions */}
      <div>
        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', display: 'block', marginBottom: 8 }}>
          Actions blocked during impersonation
        </label>
        <div className="space-y-2">
          {validActions.map(action => {
            const checked = (config.blockedActions || []).includes(action);
            return (
              <label key={action} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px',
                background: 'var(--bg-raised)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 8,
                cursor: isSuperAdmin ? 'pointer' : 'not-allowed',
                opacity: isSuperAdmin ? 1 : 0.7,
              }}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={!isSuperAdmin}
                  onChange={() => toggleAction(action)}
                />
                <span style={{ fontSize: 13, color: 'var(--text-primary)', fontFamily: 'monospace' }}>
                  {action}
                </span>
              </label>
            );
          })}
        </div>
        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
          Recommended defaults: PASSWORD_CHANGE, ROLE_CHANGE, USER_DELETE, EMAIL_CHANGE.
        </p>
      </div>

      <div className="flex items-center justify-end gap-2 pt-4"
        style={{ borderTop: '1px solid var(--border-subtle)' }}>
        <Button variant="secondary" onClick={onClose}>
          {isSuperAdmin ? 'Cancel' : 'Close'}
        </Button>
        {isSuperAdmin && (
          <Button onClick={handleSave} loading={saving}>Save Changes</Button>
        )}
      </div>
    </div>
  );
}

// ─── Main Settings page ───────────────────────────────────────────────────────
// ─── Cache (Performance) configuration modal content ──────────────────────────
// Editable cache lifetimes (minutes). Only categories the backend ACTUALLY caches
// are shown — dashboard stats & dropdowns are read straight from the DB (no cache
// layer), so exposing TTL knobs for them would mislead the operator.
const CACHE_TTL_FIELDS = [
  { key: 'list',    label: 'Lists',        hint: 'Candidate / client / requirement / submission lists' },
  { key: 'entity',  label: 'Detail views', hint: 'Single-record detail pages' },
  { key: 'reports', label: 'Reports',      hint: 'Heavy aggregations (auto-refreshed on writes)' },
];
const CACHE_TTL_MAX = 43200; // 30 days — must match backend MAX_TTL_MINUTES
const TTL_INPUT_STYLE = {
  width: 88, background: 'var(--bg-input)', border: '1px solid var(--border-input)',
  borderRadius: 8, padding: '6px 10px', fontSize: 13, color: 'var(--text-primary)',
  outline: 'none', textAlign: 'right',
};
const EMPTY_TTLS = { global: '', list: '', entity: '', reports: '' };
const ttlsToState = (t = {}) => ({
  global:    t.global == null ? '' : String(t.global),
  list:      t.list      == null ? '' : String(t.list),
  entity:    t.entity    == null ? '' : String(t.entity),
  reports:   t.reports   == null ? '' : String(t.reports),
});

function CacheConfigContent({ isDark, onClose }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SuperAdmin';

  const [enabled, setEnabled] = useState(true);
  const [ttls, setTtls]       = useState(EMPTY_TTLS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [savingTtls, setSavingTtls] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    settingsAPI.getCacheConfig()
      .then(({ data }) => {
        setEnabled(data.config?.enabled !== false);
        setTtls(ttlsToState(data.config?.ttls));
        setLoadError(false);
      })
      .catch(() => { setLoadError(true); toast.error('Failed to load cache setting'); })
      .finally(() => setLoading(false));
  }, []);

  // Single switch → persist immediately (optimistic, revert on failure).
  const toggle = async (val) => {
    if (!isSuperAdmin || saving) return;
    const prev = enabled;
    setEnabled(val);
    setSaving(true);
    try {
      await settingsAPI.updateCacheConfig(val);
      toast.success(`Caching turned ${val ? 'ON' : 'OFF'}.`);
    } catch (err) {
      setEnabled(prev);
      toast.error(err.response?.data?.message || 'Failed to update cache setting.');
    } finally {
      setSaving(false);
    }
  };

  // TTL editor → validate all fields, then persist enabled + ttls together.
  const asInt = (v) => { const n = Number(v); return Number.isInteger(n) ? n : NaN; };
  const saveTtls = async () => {
    if (!isSuperAdmin || savingTtls) return;

    // Categories are required, 1–CACHE_TTL_MAX minutes.
    for (const f of CACHE_TTL_FIELDS) {
      const n = asInt(ttls[f.key]);
      if (!(n >= 1 && n <= CACHE_TTL_MAX)) {
        toast.error(`"${f.label}" must be a whole number of minutes (1–${CACHE_TTL_MAX}).`);
        return;
      }
    }
    // Global override is optional; blank = no override.
    let global = null;
    if (String(ttls.global).trim() !== '') {
      const g = asInt(ttls.global);
      if (!(g >= 1 && g <= CACHE_TTL_MAX)) {
        toast.error(`Global override must be a whole number (1–${CACHE_TTL_MAX}) or left blank.`);
        return;
      }
      global = g;
    }

    // Send ONLY ttls — never `enabled`. The backend preserves the currently
    // persisted on/off flag when `enabled` is omitted, so saving lifetimes can't
    // clobber a caching toggle someone flipped while this modal was open.
    const payload = {
      ttls: {
        global,
        list:    asInt(ttls.list),
        entity:  asInt(ttls.entity),
        reports: asInt(ttls.reports),
      },
    };
    setSavingTtls(true);
    try {
      const { data } = await settingsAPI.updateCacheConfig(payload);
      // Re-sync BOTH values from the canonical response: TTLs to what was saved,
      // and the toggle to the true persisted state (which may have changed
      // elsewhere while the modal was open).
      setTtls(ttlsToState(data.config?.ttls));
      if (typeof data.config?.enabled === 'boolean') setEnabled(data.config.enabled);
      toast.success('Cache lifetimes saved.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save cache lifetimes.');
    } finally {
      setSavingTtls(false);
    }
  };

  if (loading) return <div className="flex justify-center py-10"><Spinner /></div>;

  // Don't let a SuperAdmin toggle against an unknown state if the load failed.
  if (loadError) {
    return (
      <div className="space-y-4" style={{ textAlign: 'center', padding: '24px 8px' }}>
        <div style={{ fontSize: 30 }}>⚠️</div>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Couldn't load the current caching status. Please retry.</p>
        <div className="flex items-center justify-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => {
            setLoading(true); setLoadError(false);
            settingsAPI.getCacheConfig()
              .then(({ data }) => {
                setEnabled(data.config?.enabled !== false);
                setTtls(ttlsToState(data.config?.ttls));
              })
              .catch(() => setLoadError(true))
              .finally(() => setLoading(false));
          }}>Retry</Button>
          <Button variant="secondary" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info banner */}
      <div style={{
        padding: '12px 14px',
        background: isDark ? 'rgba(6,182,212,0.08)' : 'rgba(6,182,212,0.06)',
        border: `1px solid ${isDark ? 'rgba(6,182,212,0.25)' : 'rgba(6,182,212,0.3)'}`,
        borderRadius: 10, display: 'flex', gap: 10, alignItems: 'flex-start',
      }}>
        <span style={{ fontSize: 16, lineHeight: 1.4 }}>ℹ️</span>
        <p style={{ fontSize: 12, color: isDark ? '#67e8f9' : '#155e75', margin: 0, lineHeight: 1.6 }}>
          {isSuperAdmin
            ? 'When ON, frequently-read data (lists, reports, dashboards) is served from Redis to cut database load. When OFF, every request reads directly from the database — exactly like before caching existed. Writes stay consistent either way; turning caching off also clears any cached data.'
            : 'This setting is managed by a SuperAdmin.'}
        </p>
      </div>

      {/* The single toggle row */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        padding: '14px 16px', background: 'var(--bg-raised)',
        border: '1px solid var(--border-subtle)', borderRadius: 10,
        opacity: isSuperAdmin ? 1 : 0.6,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 22, lineHeight: 1.2 }}>⚡</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', margin: 0, overflowWrap: 'anywhere' }}>Redis Caching</p>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3, lineHeight: 1.5, overflowWrap: 'anywhere' }}>
              Serve reads from the cache. Turn off to route every request straight to the database.
            </p>
          </div>
        </div>
        <button
          type="button"
          disabled={!isSuperAdmin || saving || savingTtls}
          onClick={() => toggle(!enabled)}
          title={!isSuperAdmin ? 'Only SuperAdmin can change this' : (enabled ? 'Click to turn caching OFF' : 'Click to turn caching ON')}
          style={{
            position: 'relative', flexShrink: 0, width: 48, height: 26, borderRadius: 13,
            background: enabled ? '#10b981' : (isDark ? 'rgba(255,255,255,0.12)' : '#d1d5db'),
            border: 'none', cursor: (!isSuperAdmin || saving || savingTtls) ? 'not-allowed' : 'pointer',
            transition: 'background 0.2s', padding: 0, outline: 'none',
          }}
        >
          <span style={{
            position: 'absolute', top: 3, left: enabled ? 25 : 3,
            width: 20, height: 20, borderRadius: '50%', background: '#ffffff',
            boxShadow: '0 1px 4px rgba(0,0,0,0.25)', transition: 'left 0.2s', display: 'block',
          }} />
        </button>
      </div>

      {/* Status summary */}
      <div style={{
        padding: '10px 14px', background: 'var(--bg-raised)',
        border: '1px solid var(--border-subtle)', borderRadius: 8,
        fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6,
      }}>
        Caching is currently <strong style={{ color: enabled ? '#10b981' : '#ef4444' }}>{enabled ? 'ON' : 'OFF'}</strong>
        {enabled ? ' — reads are served from Redis where possible.' : ' — all reads go directly to the database.'}
      </div>

      {/* ── Cache lifetimes (TTL) ──────────────────────────────────────────── */}
      <div style={{
        padding: '14px 16px', background: 'var(--bg-raised)',
        border: '1px solid var(--border-subtle)', borderRadius: 10,
        opacity: isSuperAdmin ? 1 : 0.6,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 18 }}>⏱️</span>
          <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Cache lifetimes (TTL)</p>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.6 }}>
          How long each kind of data may stay cached, in minutes. This is only a safety-net — every edit
          already refreshes the affected data instantly, so longer values won't show stale content in normal
          use. Set a <strong>Global override</strong> to force one lifetime everywhere.
        </p>

        {/* Global override */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          padding: '8px 0', borderBottom: '1px dashed var(--border-subtle)',
        }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Global override</p>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 0', lineHeight: 1.5, overflowWrap: 'anywhere' }}>
              Blank = use the per-type values below. Set it to apply one lifetime to everything.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <input
              type="number" min={1} max={CACHE_TTL_MAX} step={1} placeholder="—"
              value={ttls.global}
              disabled={!isSuperAdmin || savingTtls}
              onChange={(e) => setTtls(p => ({ ...p, global: e.target.value }))}
              style={TTL_INPUT_STYLE}
            />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>min</span>
          </div>
        </div>

        {/* Per-category */}
        {CACHE_TTL_FIELDS.map((f, i) => {
          const overridden = String(ttls.global).trim() !== '';
          return (
            <div key={f.key} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
              padding: '8px 0',
              borderBottom: i < CACHE_TTL_FIELDS.length - 1 ? '1px solid var(--border-subtle)' : 'none',
            }}>
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', margin: 0, overflowWrap: 'anywhere' }}>{f.label}</p>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 0', lineHeight: 1.5, overflowWrap: 'anywhere' }}>{f.hint}</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, opacity: overridden ? 0.45 : 1 }}>
                <input
                  type="number" min={1} max={CACHE_TTL_MAX} step={1}
                  value={ttls[f.key]}
                  disabled={!isSuperAdmin || savingTtls}
                  onChange={(e) => setTtls(p => ({ ...p, [f.key]: e.target.value }))}
                  title={overridden ? 'Ignored while a Global override is set' : undefined}
                  style={TTL_INPUT_STYLE}
                />
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>min</span>
              </div>
            </div>
          );
        })}

        {isSuperAdmin && (
          <div className="flex items-center justify-end" style={{ marginTop: 14 }}>
            <Button variant="primary" size="sm" onClick={saveTtls} disabled={saving || savingTtls}>
              {savingTtls ? 'Saving…' : 'Save lifetimes'}
            </Button>
          </div>
        )}
      </div>

      {/* Footer — changes persist on toggle, so just Close */}
      <div className="flex items-center justify-end gap-2 pt-4" style={{ borderTop: '1px solid var(--border-subtle)' }}>
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </div>
    </div>
  );
}

// ─── Downloads (file) configuration modal content ─────────────────────────────
const RESUME_TTL_MIN = 30;     // must match backend RESUME_TTL_MIN
const RESUME_TTL_MAX = 86400;  // must match backend RESUME_TTL_MAX
function FileConfigContent({ isDark, onClose }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SuperAdmin';

  const [ttl, setTtl]         = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = () => {
    setLoading(true); setLoadError(false);
    settingsAPI.getFileConfig()
      .then(({ data }) => setTtl(String(data.config?.resumeUrlTtlSeconds ?? '')))
      .catch(() => { setLoadError(true); toast.error('Failed to load download setting'); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async () => {
    if (!isSuperAdmin || saving) return;
    const n = Number(ttl);
    if (!Number.isInteger(n) || n < RESUME_TTL_MIN || n > RESUME_TTL_MAX) {
      toast.error(`Link lifetime must be a whole number ${RESUME_TTL_MIN}–${RESUME_TTL_MAX} seconds.`);
      return;
    }
    setSaving(true);
    try {
      const { data } = await settingsAPI.updateFileConfig({ resumeUrlTtlSeconds: n });
      setTtl(String(data.config?.resumeUrlTtlSeconds ?? n));
      toast.success('Download settings saved.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save download setting.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-10"><Spinner /></div>;

  if (loadError) {
    return (
      <div className="space-y-4" style={{ textAlign: 'center', padding: '24px 8px' }}>
        <div style={{ fontSize: 30 }}>⚠️</div>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Couldn't load the current setting. Please retry.</p>
        <div className="flex items-center justify-center gap-2">
          <Button variant="secondary" size="sm" onClick={load}>Retry</Button>
          <Button variant="secondary" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    );
  }

  const mins = Number.isInteger(Number(ttl)) && Number(ttl) > 0 ? (Number(ttl) / 60) : null;

  return (
    <div className="space-y-6">
      {/* Info banner */}
      <div style={{
        padding: '12px 14px',
        background: isDark ? 'rgba(168,85,247,0.08)' : 'rgba(168,85,247,0.06)',
        border: `1px solid ${isDark ? 'rgba(168,85,247,0.25)' : 'rgba(168,85,247,0.3)'}`,
        borderRadius: 10, display: 'flex', gap: 10, alignItems: 'flex-start',
      }}>
        <span style={{ fontSize: 16, lineHeight: 1.4 }}>ℹ️</span>
        <p style={{ fontSize: 12, color: isDark ? '#d8b4fe' : '#6b21a8', margin: 0, lineHeight: 1.6 }}>
          {isSuperAdmin
            ? 'Resume view/download links are short-lived for security. This sets how long a generated link stays valid before it expires. Shorter is safer; longer is more convenient.'
            : 'This setting is managed by a SuperAdmin.'}
        </p>
      </div>

      {/* The single field */}
      <div style={{
        padding: '14px 16px', background: 'var(--bg-raised)',
        border: '1px solid var(--border-subtle)', borderRadius: 10,
        opacity: isSuperAdmin ? 1 : 0.6,
      }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', display: 'block', marginBottom: 6 }}>
          Resume link lifetime (seconds)
        </label>
        <input
          type="number" min={RESUME_TTL_MIN} max={RESUME_TTL_MAX} step={1}
          value={ttl}
          disabled={!isSuperAdmin || saving}
          onChange={(e) => setTtl(e.target.value)}
          style={{
            width: '100%', background: 'var(--bg-input)', border: '1px solid var(--border-input)',
            borderRadius: 8, padding: '8px 12px', fontSize: 13, color: 'var(--text-primary)', outline: 'none',
          }}
        />
        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
          Range: {RESUME_TTL_MIN}–{RESUME_TTL_MAX} seconds. Default: 300 (5 min).
          {mins != null && ` Currently ≈ ${mins % 1 === 0 ? mins : mins.toFixed(1)} min.`}
        </p>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 pt-4" style={{ borderTop: '1px solid var(--border-subtle)' }}>
        <Button variant="secondary" onClick={onClose}>Close</Button>
        {isSuperAdmin && (
          <Button variant="primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Teams Meetings configuration modal content ───────────────────────────────
// Microsoft Graph credentials for auto-creating Teams join links on interview
// emails. The client secret is WRITE-ONLY: the API never returns it — the UI
// only shows whether one is saved, and sends a new value only when typed.
// Chip input for Teams co-owners. Deliberately NOT the shared EmailTagInput:
// that one validates emails only, and co-owners may also be pasted as an Entra
// Object ID (GUID) — which is the only option for tenants that can't grant the
// User.Read.All permission needed to resolve an email.
const CO_OWNER_GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isCoOwnerValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || CO_OWNER_GUID_RE.test(v);

function CoOwnerTagInput({ value = [], onChange, disabled, isDark }) {
  const [input, setInput]     = useState('');
  const [invalid, setInvalid] = useState('');

  const commit = (raw) => {
    const parts = String(raw).split(/[,;\s]+/).map(p => p.trim().toLowerCase()).filter(Boolean);
    if (!parts.length) return;
    const good = [], bad = [];
    for (const p of parts) (isCoOwnerValid(p) ? good : bad).push(p);
    const next = [...value];
    for (const g of good) if (!next.includes(g)) next.push(g);
    if (next.length !== value.length) onChange(next);
    if (bad.length) { setInvalid(bad[0]); setInput(bad.join(', ')); }
    else { setInvalid(''); setInput(''); }
  };

  return (
    <div>
      <div
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', minHeight: 40,
          background: 'var(--bg-input)', border: '1px solid var(--border-input)',
          borderRadius: 8, padding: '6px 10px', cursor: disabled ? 'not-allowed' : 'text',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {value.map((tag, i) => (
          <span key={tag} style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px',
            borderRadius: 99, fontSize: 11, fontWeight: 600,
            background: isDark ? 'rgba(99,102,241,0.2)' : '#e0e7ff',
            color: isDark ? '#a5b4fc' : '#4338ca',
            border: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'}`,
            maxWidth: '100%',
          }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{tag}</span>
            {!disabled && (
              <span
                onClick={() => onChange(value.filter((_, k) => k !== i))}
                style={{ cursor: 'pointer', fontSize: 13, lineHeight: 1 }}
              >×</span>
            )}
          </span>
        ))}
        <input
          value={input}
          disabled={disabled}
          onChange={(e) => { setInput(e.target.value); if (invalid) setInvalid(''); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(input); }
            if (e.key === 'Backspace' && !input && value.length) onChange(value.slice(0, -1));
          }}
          onBlur={() => commit(input)}
          placeholder={value.length === 0 ? 'manager@yourcompany.com — press Enter to add more' : ''}
          style={{
            flex: 1, minWidth: 180, border: 'none', outline: 'none',
            background: 'transparent', fontSize: 13, color: 'var(--text-primary)',
          }}
        />
      </div>
      {invalid && (
        <p style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>
          “{invalid}” isn’t a valid email address or Object ID.
        </p>
      )}
    </div>
  );
}

function TeamsConfigContent({ isDark, onClose }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SuperAdmin';

  const [form, setForm]           = useState({ tenantId: '', clientId: '', organizer: '', clientSecret: '', recordAutomatically: true, coOrganizers: [], recordingsAccount: '' });
  const [secretSet, setSecretSet] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [permissions, setPermissions] = useState(null);   // [{ role, purpose, granted }] | { error } | null
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = () => {
    setLoading(true); setLoadError(false);
    settingsAPI.getTeamsConfig()
      .then(({ data }) => {
        const c = data.config || {};
        setForm({
          tenantId: c.tenantId || '', clientId: c.clientId || '', organizer: c.organizer || '', clientSecret: '',
          // Absent (older API response) → treat as ON, matching the server default.
          recordAutomatically: c.recordAutomatically !== false,
          coOrganizers: Array.isArray(c.coOrganizers) ? c.coOrganizers : [],
          recordingsAccount: c.recordingsAccount || '',
        });
        setSecretSet(!!c.clientSecretSet);
        setConfigured(!!c.configured);
        setPermissions(c.permissions || null);
      })
      .catch(() => { setLoadError(true); toast.error('Failed to load Teams settings'); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async () => {
    if (!isSuperAdmin || saving) return;
    setSaving(true);
    try {
      const payload = {
        tenantId:  form.tenantId.trim(),
        clientId:  form.clientId.trim(),
        organizer: form.organizer.trim(),
        recordAutomatically: !!form.recordAutomatically,
        coOrganizers: form.coOrganizers,
        recordingsAccount: form.recordingsAccount.trim(),
      };
      // Only send the secret when a new one was typed — empty keeps the saved one.
      if (form.clientSecret.trim()) payload.clientSecret = form.clientSecret.trim();
      const { data } = await settingsAPI.updateTeamsConfig(payload);
      setSecretSet(!!data.config?.clientSecretSet);
      setConfigured(!!data.config?.configured);
      setForm(f => ({ ...f, clientSecret: '' }));   // never keep the typed secret around
      toast.success('Teams settings saved.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save Teams settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-10"><Spinner /></div>;

  if (loadError) {
    return (
      <div className="space-y-4" style={{ textAlign: 'center', padding: '24px 8px' }}>
        <div style={{ fontSize: 30 }}>⚠️</div>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Couldn't load the current settings. Please retry.</p>
        <div className="flex items-center justify-center gap-2">
          <Button variant="secondary" size="sm" onClick={load}>Retry</Button>
          <Button variant="secondary" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    );
  }

  const inputStyle = {
    width: '100%', background: 'var(--bg-input)', border: '1px solid var(--border-input)',
    borderRadius: 8, padding: '8px 12px', fontSize: 13, color: 'var(--text-primary)', outline: 'none',
  };
  const FIELDS = [
    { key: 'tenantId',  label: 'Tenant ID (Directory ID)',       placeholder: 'e.g. 3f2504e0-4f89-11d3-9a0c-0305e82c3301', hint: 'Azure Portal → Entra ID → App registration → Overview.' },
    { key: 'clientId',  label: 'Client ID (Application ID)',     placeholder: 'e.g. 6ba7b810-9dad-11d1-80b4-00c04fd430c8', hint: 'From the same Overview page.' },
    { key: 'organizer', label: 'Organizer (email or Object ID)', placeholder: 'recruiter@yourcompany.com  ·  or the user\'s Object ID (GUID)', hint: 'Licensed user the meetings are created under (needs the Teams access policy). Email requires the User.Read.All application permission to auto-resolve; the Object ID (Entra ID → Users) works without it.' },
  ];

  return (
    <div className="space-y-5">
      {/* Status banner */}
      <div style={{
        padding: '12px 14px',
        background: configured ? (isDark ? 'rgba(16,185,129,0.08)' : 'rgba(16,185,129,0.07)') : (isDark ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.06)'),
        border: `1px solid ${configured ? 'rgba(16,185,129,0.3)' : 'rgba(59,130,246,0.3)'}`,
        borderRadius: 10, display: 'flex', gap: 10, alignItems: 'flex-start',
      }}>
        <span style={{ fontSize: 16, lineHeight: 1.4 }}>{configured ? '✅' : 'ℹ️'}</span>
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>
          {configured
            ? 'Teams is configured — Online interviews automatically get a Teams join link in the email and calendar invite.'
            : 'Fill all four values to auto-create Microsoft Teams join links for Online interviews. Until then, interview emails send without a join link. Requires the one-time Azure app registration (Graph application permissions OnlineMeetings.ReadWrite.All and, for interview recordings, Files.Read.All + Teams access policy).'}
        </p>
      </div>

      {/* What the Azure app is actually allowed to do, read from its own token.
          Each missing line is a feature that is silently off. */}
      {configured && permissions && (
        <div className="rounded-xl px-4 py-3" style={{ background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)' }}>
          <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-primary)', margin: 0 }}>Graph permissions granted to the app</p>
          {permissions.error ? (
            <p className="text-xs" style={{ color: 'var(--text-muted)', margin: 0 }}>Could not check: {permissions.error}</p>
          ) : (
            <ul className="text-xs space-y-1" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {permissions.map(p => (
                <li key={p.role} className="flex items-start gap-2">
                  <span aria-hidden="true">{p.granted ? '✅' : '❌'}</span>
                  <span style={{ color: p.granted ? 'var(--text-secondary)' : 'var(--text-primary)' }}>
                    <code style={{ fontSize: 11 }}>{p.role}</code> — {p.purpose}
                    {!p.granted && <span style={{ color: '#ef4444' }}> · missing: grant it in Entra ID → App registrations → API permissions (admin consent)</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {FIELDS.map(f => (
        <div key={f.key} style={{ opacity: isSuperAdmin ? 1 : 0.6 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', display: 'block', marginBottom: 6 }}>{f.label}</label>
          <input
            type="text" autoComplete="off" spellCheck={false}
            value={form[f.key]}
            disabled={!isSuperAdmin || saving}
            placeholder={f.placeholder}
            onChange={(e) => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
            style={inputStyle}
          />
          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 5 }}>{f.hint}</p>
        </div>
      ))}

      {/* Client secret — write-only */}
      <div style={{ opacity: isSuperAdmin ? 1 : 0.6 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', display: 'block', marginBottom: 6 }}>
          Client Secret {secretSet && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, color: '#10b981' }}>✓ saved</span>}
        </label>
        <input
          type="password" autoComplete="new-password" spellCheck={false}
          value={form.clientSecret}
          disabled={!isSuperAdmin || saving}
          placeholder={secretSet ? '•••••••••••• (leave blank to keep the saved secret)' : 'Paste the secret VALUE from Certificates & secrets'}
          onChange={(e) => setForm(prev => ({ ...prev, clientSecret: e.target.value }))}
          style={inputStyle}
        />
        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 5, lineHeight: 1.5 }}>
          Write-only for security — it is never shown again after saving. Type a new value only to replace it.
          Note the secret's expiry date in Azure; when it lapses, join links silently stop being created.
        </p>
      </div>

      {/* Co-owners (co-organizers) added to every interview meeting */}
      <div>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 5 }}>
          Meeting co-owners
        </label>
        <CoOwnerTagInput
          value={form.coOrganizers}
          disabled={!isSuperAdmin}
          isDark={isDark}
          onChange={(next) => setForm(prev => ({ ...prev, coOrganizers: next }))}
        />
        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 5, lineHeight: 1.5 }}>
          Added as co-organizers to every interview meeting — they can start/stop recording, manage the
          lobby, change meeting options and open the recording afterwards. They also receive the interview
          invitation email with a calendar invite (Teams itself puts nothing on a co-organizer's calendar).
          Must be licensed users in <strong>your own tenant</strong> (external interviewers can't be
          co-owners). Paste an Object ID instead of an email if the app doesn't have the User.Read.All
          permission — note an Object ID can't receive the email invite. Anyone who can't be resolved is
          skipped — the meeting is still created.
        </p>
      </div>

      {/* Auto-record interview meetings */}
      <label
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, cursor: isSuperAdmin ? 'pointer' : 'not-allowed',
          padding: '12px 14px', borderRadius: 10,
          background: form.recordAutomatically ? (isDark ? 'rgba(239,68,68,0.07)' : '#fef2f2') : 'var(--bg-raised)',
          border: `1px solid ${form.recordAutomatically ? (isDark ? 'rgba(239,68,68,0.28)' : '#fecaca') : 'var(--border-subtle)'}`,
        }}
      >
        <input
          type="checkbox"
          checked={!!form.recordAutomatically}
          disabled={!isSuperAdmin}
          onChange={(e) => setForm(prev => ({ ...prev, recordAutomatically: e.target.checked }))}
          style={{ width: 15, height: 15, marginTop: 2, accentColor: '#6366f1', flexShrink: 0 }}
        />
        <span>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', display: 'block' }}>
            ⏺ Record interview meetings automatically
          </span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5, display: 'block', marginTop: 3 }}>
            Teams starts recording as soon as the interview begins; recordings land in the organizer's OneDrive
            (or a co-organizer's, when the organizer has none). Requires cloud recording to be permitted by your
            Teams meeting policy — if it isn't, the meeting is still created, just not recorded. Make sure
            candidates are told they'll be recorded.
          </span>
        </span>
      </label>

      {/* Where "View recording" looks */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
          Recordings account (email or Object ID)
        </label>
        <input
          type="text"
          value={form.recordingsAccount}
          disabled={!isSuperAdmin}
          onChange={(e) => setForm(prev => ({ ...prev, recordingsAccount: e.target.value }))}
          placeholder={form.organizer ? `blank = organizer (${form.organizer})` : 'e.g. abhinav@yourcompany.com'}
          style={inputStyle}
        />
        <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '5px 0 0', lineHeight: 1.5 }}>
          The account whose OneDrive Teams saves interview recordings to — the "View recording" button reads only
          this account's Recordings folder. Teams' meeting chat says it after each meeting ("Recording has been saved
          to …'s OneDrive"). Needs the Files.Read.All permission above.
        </p>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 pt-4" style={{ borderTop: '1px solid var(--border-subtle)' }}>
        <Button variant="secondary" onClick={onClose}>Close</Button>
        {isSuperAdmin && (
          <Button variant="primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Database Backups modal content ───────────────────────────────────────────
const fmtBytes = (n) => {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const BACKUP_FREQS = [
  { value: 'every6h',  label: 'Every 6 hours (4× a day)' },
  { value: 'every12h', label: 'Every 12 hours (2× a day)' },
  { value: 'daily',    label: 'Every day' },
  { value: 'weekly',   label: 'Every week' },
];
const BACKUP_RETENTIONS = [
  { value: 7,   label: '7 days' },
  { value: 30,  label: '30 days' },
  { value: 90,  label: '90 days' },
  { value: 180, label: '6 months' },
  { value: 365, label: '1 year' },
];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Local YYYY-MM-DD. Deliberately NOT toISOString().slice(0,10), which converts
// to UTC first and so reports 'yesterday' for anyone east of Greenwich after
// midnight — IST included.
const ymd = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return ymd(d); };

const BACKUP_DATE_PRESETS = [
  { label: 'Today',      range: () => ({ from: ymd(new Date()), to: ymd(new Date()) }) },
  { label: 'Last 7 days', range: () => ({ from: daysAgo(6),      to: ymd(new Date()) }) },
  { label: 'Last 30 days',range: () => ({ from: daysAgo(29),     to: ymd(new Date()) }) },
];

const dateInputStyle = (isDark) => ({
  fontSize: 12, padding: '5px 8px', borderRadius: 8,
  border: '1px solid var(--border-subtle)',
  background: isDark ? 'rgba(255,255,255,0.05)' : '#fff',
  color: 'var(--text-primary)', colorScheme: isDark ? 'dark' : 'light',
});

/**
 * A date-range shortcut. Its own component because a flat pill with nothing but
 * cursor:pointer reads as a LABEL — people could not tell these were clickable.
 * It now responds on hover, presses in on click, shows keyboard focus, and
 * stays highlighted while its range is the one in effect.
 */
function PresetButton({ isDark, onClick, danger = false, active = false, children }) {
  const [hover, setHover] = useState(false);
  const [down,  setDown]  = useState(false);

  const accent = danger ? (isDark ? '#fca5a5' : '#b91c1c') : (isDark ? '#a5b4fc' : '#4f46e5');
  const idle   = isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc';
  const lit    = danger
    ? (isDark ? 'rgba(239,68,68,0.15)' : '#fef2f2')
    : (isDark ? 'rgba(99,102,241,0.18)' : '#eef2ff');

  const on = hover || active;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setDown(false); }}
      onMouseDown={() => setDown(true)}
      onMouseUp={() => setDown(false)}
      style={{
        fontSize: 11, fontWeight: 600, padding: '5px 10px', borderRadius: 99,
        cursor: 'pointer', whiteSpace: 'nowrap',
        border: '1px solid',
        borderColor: on ? accent : 'var(--border-subtle)',
        background: on ? lit : idle,
        color: on ? accent : (danger ? accent : 'var(--text-secondary)'),
        // Pressing in is the cue that the click registered — without it a
        // preset that yields no rows looks like nothing happened at all.
        transform: down ? 'scale(0.96)' : 'scale(1)',
        boxShadow: down ? 'none' : (hover ? '0 1px 4px rgba(0,0,0,0.10)' : 'none'),
        transition: 'background 120ms, border-color 120ms, color 120ms, transform 80ms, box-shadow 120ms',
        outlineOffset: 2,
      }}
    >
      {children}
    </button>
  );
}

/**
 * Backups whose createdAt falls inside [from, to], both bounds inclusive and
 * expanded to whole local days. Comparing formatted local dates rather than
 * Date objects keeps the boundary correct regardless of timezone: an S3 object
 * stamped 23:40 IST must count as that day, not the next one in UTC.
 */
const filterBackupsByDate = (backups, from, to) => {
  if (!from && !to) return backups;
  return backups.filter(b => {
    const d = new Date(b.createdAt);
    if (isNaN(d.getTime())) return true;   // never hide a backup we cannot date
    const day = ymd(d);
    if (from && day < from) return false;
    if (to   && day > to)   return false;
    return true;
  });
};

function DbBackupContent({ isDark, onClose }) {
  const [loading, setLoading]   = useState(true);
  const [backups, setBackups]   = useState([]);
  const [status, setStatus]     = useState({});
  // Schedule + retention — loaded from the server, saved via the button below.
  const [sched, setSched] = useState({ frequency: 'daily', hour: 2, minute: 0, weekday: 0, retentionDays: 30 });
  const [savingSched, setSavingSched] = useState(false);
  // Date filter. Retention runs to a year and every6h makes 4 backups a day,
  // so the list can reach ~1,460 rows — finding the one from a given morning
  // by scrolling is not realistic. Filtered client-side: the full list is
  // already loaded, so this is instant and needs no API change.
  const [fromDate, setFromDate] = useState('');
  const [toDate,   setToDate]   = useState('');
  // Recomputed per render: the list is small and this keeps the count badge and
  // the rows from ever disagreeing.
  const todayStr = ymd(new Date());
  const visibleBackups = filterBackupsByDate(backups, fromDate, toDate);
  // Restore flow: which key is pending + the typed confirmation.
  const [restoreKey, setRestoreKey]   = useState(null);
  const [confirmText, setConfirmText] = useState('');
  // Backup/restore run as background jobs on the server (a restore can take
  // far longer than the 60 s proxy timeout). `job` is the latest job as the
  // server reports it; while it is running we poll every 3 s. `watchedJobId`
  // is the job whose outcome we should announce — the one started (or found
  // running) while this panel is open, never an old finished one.
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(null);   // 'backup' | 'restore' while the POST is in flight
  const watchedJobId = useRef(null);
  const jobRunning = job?.status === 'running';
  const backingUp  = starting === 'backup'  || (jobRunning && job.op === 'backup');
  const restoring  = starting === 'restore' || (jobRunning && job.op === 'restore');

  const load = useCallback(() => {
    setLoading(true);
    settingsAPI.listDbBackups()
      .then(({ data }) => {
        setBackups(data.backups || []);
        setStatus(data.status || {});
        if (data.config) setSched(s => ({ ...s, ...data.config }));
      })
      .catch(() => toast.error('Could not load backups. Check S3 access.'))
      .finally(() => setLoading(false));
  }, []);

  const saveSchedule = async () => {
    setSavingSched(true);
    try {
      const { data } = await settingsAPI.updateDbBackupConfig(sched);
      if (data.config) setSched(s => ({ ...s, ...data.config }));
      toast.success('Backup schedule saved — applied immediately.');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not save the schedule.');
    } finally {
      setSavingSched(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  // Fetch the latest job. A job that is still running (started here, by
  // another admin, or before this panel was reopened) is watched so its
  // outcome is announced; an already-finished one is shown silently.
  const refreshJob = useCallback(() => {
    settingsAPI.getDbBackupJob()
      .then(({ data }) => {
        const j = data.job || null;
        if (j?.status === 'running') watchedJobId.current = j.id;
        setJob(j);
      })
      .catch(() => { /* status is best-effort; the POSTs still guard with 409 */ });
  }, []);

  useEffect(() => { refreshJob(); }, [refreshJob]);

  // Poll every 3 s while a job is running (the job lives in the DB, so any pod
  // can answer). A failed poll is ignored — the next tick retries.
  useEffect(() => {
    if (!jobRunning) return undefined;
    const t = setInterval(refreshJob, 3000);
    return () => clearInterval(t);
  }, [jobRunning, refreshJob]);

  // Announce the outcome once the watched job finishes.
  useEffect(() => {
    if (!job || job.status === 'running' || job.id !== watchedJobId.current) return;
    watchedJobId.current = null;
    const isRestore = job.op === 'restore';
    // A restore rewrites the tables behind every cached list/detail, so drop
    // the whole frontend cache — also on failure, as pg_restore may have
    // applied part of the dump before it stopped.
    if (isRestore) bustCache('');
    if (job.status === 'succeeded') {
      toast.success(isRestore
        ? 'Database restored. A safety backup of the previous data was saved first.'
        : 'Backup created.');
    } else {
      toast.error(job.error || (isRestore ? 'Restore failed.' : 'Backup failed.'));
    }
    load();
  }, [job, load]);

  const startJob = (j) => {
    watchedJobId.current = j.id;
    setJob(j);
  };

  const backupNow = async () => {
    setStarting('backup');
    try {
      const { data } = await settingsAPI.runDbBackup();
      if (data.job) {
        startJob(data.job);
        toast('Backup started — this can take a few minutes.');
      } else {
        toast.success('Backup created.');
        load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Backup could not be started.');
      // 409 (another backup/restore is running) or a lost response: pick up
      // whatever job is running so the panel shows it instead of guessing.
      refreshJob();
    } finally {
      setStarting(null);
    }
  };

  const doRestore = async () => {
    if (confirmText !== 'RESTORE') return;
    setStarting('restore');
    try {
      const { data } = await settingsAPI.restoreDbBackup(restoreKey, confirmText);
      // Close the confirm panel at once so a second restore is never one click away.
      setRestoreKey(null);
      setConfirmText('');
      if (data.job) {
        startJob(data.job);
        toast('Restore started — this can take several minutes. Keep this window open to see the result.');
      } else {
        bustCache('');
        toast.success('Database restored. A safety backup of the previous data was saved first.');
        load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Restore could not be started.');
      refreshJob();
    } finally {
      setStarting(null);
    }
  };

  const shortName = (key) => (key || '').split('/').pop();

  return (
    <div className="space-y-5">
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        The database is backed up to S3 automatically — <strong>
        {(BACKUP_FREQS.find(f => f.value === sched.frequency)?.label || 'Every day').toLowerCase()}
        {sched.frequency === 'weekly' ? ` on ${WEEKDAYS[sched.weekday]}` : ''}
        {' '}at {String(sched.hour).padStart(2, '0')}:{String(sched.minute).padStart(2, '0')}</strong>,
        keeping <strong>{BACKUP_RETENTIONS.find(r => r.value === sched.retentionDays)?.label || `${sched.retentionDays} days`}</strong> of history.
        You can also back up now, or restore any backup below.
      </p>

      {/* ── Schedule + retention controls ── */}
      <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 10, padding: 14,
        background: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc' }} className="space-y-3">
        <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>Automatic backup schedule</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>How often</label>
            <select value={sched.frequency}
              onChange={e => setSched(s => ({ ...s, frequency: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, fontSize: 13,
                background: 'var(--input-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)' }}>
              {BACKUP_FREQS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
              {sched.frequency === 'every6h' || sched.frequency === 'every12h' ? 'Starting at' : 'At time'}
            </label>
            <input type="time"
              value={`${String(sched.hour).padStart(2, '0')}:${String(sched.minute).padStart(2, '0')}`}
              onChange={e => {
                const [h, m] = String(e.target.value || '02:00').split(':').map(Number);
                setSched(s => ({ ...s, hour: Number.isInteger(h) ? h : s.hour, minute: Number.isInteger(m) ? m : s.minute }));
              }}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, fontSize: 13,
                background: 'var(--input-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)' }} />
          </div>
          {sched.frequency === 'weekly' && (
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>On which day</label>
              <select value={sched.weekday}
                onChange={e => setSched(s => ({ ...s, weekday: Number(e.target.value) }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, fontSize: 13,
                  background: 'var(--input-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)' }}>
                {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
            </div>
          )}
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Keep backups for</label>
            <select value={sched.retentionDays}
              onChange={e => setSched(s => ({ ...s, retentionDays: Number(e.target.value) }))}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, fontSize: 13,
                background: 'var(--input-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)' }}>
              {BACKUP_RETENTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Older backups are deleted automatically after the retention period — the 5 newest are always kept, no matter what.
          </p>
          <Button variant="primary" onClick={saveSchedule} disabled={savingSched}>
            {savingSched ? 'Saving…' : 'Save schedule'}
          </Button>
        </div>
      </div>

      {/* Status + Backup-now */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
        background: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc', border: '1px solid var(--border-subtle)', borderRadius: 10, padding: '12px 14px' }}>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {status.lastRunAt
            ? <>Last backup: <strong style={{ color: 'var(--text-primary)' }}>{new Date(status.lastRunAt).toLocaleString()}</strong></>
            : 'No backup has run yet this session.'}
          {status.lastError && <span style={{ color: '#ef4444', display: 'block', marginTop: 2 }}>Last error: {status.lastError}</span>}
          {jobRunning && (
            <span style={{ color: 'var(--text-primary)', display: 'block', marginTop: 2 }}>
              ⏳ {job.op === 'restore' ? 'Restore' : 'Backup'} in progress
              {job.startedAt ? ` — started ${new Date(job.startedAt).toLocaleString()}` : ''}…
            </span>
          )}
        </div>
        <Button variant="primary" onClick={backupNow} disabled={backingUp || restoring}>
          {backingUp ? 'Backing up…' : 'Back up now'}
        </Button>
      </div>

      {/* Danger note */}
      <div style={{ fontSize: 12, color: isDark ? '#fca5a5' : '#b91c1c', background: isDark ? 'rgba(239,68,68,0.08)' : '#fef2f2',
        border: `1px solid ${isDark ? 'rgba(239,68,68,0.3)' : '#fecaca'}`, borderRadius: 10, padding: '10px 12px', lineHeight: 1.6 }}>
        ⚠️ Restoring reloads this backup's tables and <strong>overwrites the current data</strong> in them. A fresh safety backup of the current data is taken automatically first. (Tables created after this backup are left as-is.)
      </div>

      {/* Date filter. Both bounds INCLUSIVE and whole-day: picking the same
          day in both boxes shows that day's backups, which is the common case.
          Capped at today because a backup cannot exist in the future. */}
      {backups.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 10 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>From</label>
            <input type="date" value={fromDate} max={toDate || todayStr}
              onChange={e => setFromDate(e.target.value)}
              style={dateInputStyle(isDark)} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>To</label>
            <input type="date" value={toDate} min={fromDate || undefined} max={todayStr}
              onChange={e => setToDate(e.target.value)}
              style={dateInputStyle(isDark)} />
          </div>
          {BACKUP_DATE_PRESETS.map(pre => {
            // Highlight the preset whose range is the one currently applied, so
            // the filter state is readable at a glance instead of having to
            // decode two date boxes.
            const r = pre.range();
            const active = fromDate === r.from && toDate === r.to;
            return (
              <PresetButton key={pre.label} isDark={isDark} active={active}
                onClick={() => { setFromDate(r.from); setToDate(r.to); }}>
                {pre.label}
              </PresetButton>
            );
          })}
          {(fromDate || toDate) && (
            <PresetButton isDark={isDark} danger
              onClick={() => { setFromDate(''); setToDate(''); }}>
              Clear
            </PresetButton>
          )}
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>
            {visibleBackups.length === backups.length
              ? `${backups.length} backup${backups.length === 1 ? '' : 's'}`
              : `${visibleBackups.length} of ${backups.length}`}
          </span>
        </div>
      )}

      {/* Backup list */}
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center' }}><Spinner /></div>
      ) : backups.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: 16 }}>No backups in S3 yet. Click “Back up now” to create the first one.</p>
      ) : (
        visibleBackups.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: 16 }}>
            No backups in that date range — widen the dates or clear the filter.
          </p>
        ) : (
        <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 10, overflow: 'hidden', maxHeight: 320, overflowY: 'auto' }}>
          {visibleBackups.map((b) => (
            <div key={b.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
              padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{shortName(b.key)}</p>
                <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>{new Date(b.createdAt).toLocaleString()} · {fmtBytes(b.size)}</p>
              </div>
              <button type="button" onClick={() => { setRestoreKey(b.key); setConfirmText(''); }}
                style={{ flexShrink: 0, fontSize: 12, fontWeight: 600, color: isDark ? '#fca5a5' : '#b91c1c',
                  background: isDark ? 'rgba(239,68,68,0.12)' : '#fef2f2', border: `1px solid ${isDark ? 'rgba(239,68,68,0.35)' : '#fecaca'}`,
                  borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>
                Restore
              </button>
            </div>
          ))}
        </div>
        )
      )}

      {/* Typed-confirm restore panel */}
      {restoreKey && (
        <div style={{ border: `1px solid ${isDark ? 'rgba(239,68,68,0.4)' : '#fca5a5'}`, borderRadius: 10, padding: 14,
          background: isDark ? 'rgba(239,68,68,0.06)' : '#fef2f2' }} className="space-y-3">
          <p style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600 }}>
            Restore <span style={{ color: isDark ? '#fca5a5' : '#b91c1c' }}>{shortName(restoreKey)}</span>?
          </p>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            This overwrites the current data in this backup's tables. Type <strong>RESTORE</strong> to confirm.
          </p>
          <input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type RESTORE"
            autoFocus
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 13,
              background: 'var(--input-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)', outline: 'none' }}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => { setRestoreKey(null); setConfirmText(''); }} disabled={starting === 'restore'}>Cancel</Button>
            <button type="button" onClick={doRestore} disabled={confirmText !== 'RESTORE' || restoring || backingUp}
              style={{ fontSize: 13, fontWeight: 600, color: '#fff',
                background: confirmText === 'RESTORE' ? '#ef4444' : (isDark ? '#4b5563' : '#cbd5e1'),
                border: 'none', borderRadius: 8, padding: '8px 18px',
                cursor: confirmText === 'RESTORE' && !restoring && !backingUp ? 'pointer' : 'not-allowed' }}>
              {restoring ? 'Restoring…' : 'Restore database'}
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-2" style={{ borderTop: '1px solid var(--border-subtle)' }}>
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </div>
    </div>
  );
}

export default function Settings() {
  const { theme } = useTheme();
  const { isSuperAdmin } = useAuth();
  const isDark = theme === 'dark';
  const [openModal, setOpenModal] = useState(null); // id of open card

  // SuperAdmin-only cards are hidden from everyone else.
  const visibleCards = SETTING_CARDS.filter(c => !c.superAdminOnly || isSuperAdmin());

  const closeModal = useCallback(() => setOpenModal(null), []);

  const anyOverlayOpen = !!openModal;
  useLayoutEffect(() => {
    if (anyOverlayOpen) {
      document.documentElement.classList.add('scroll-locked');
      return () => { document.documentElement.classList.remove('scroll-locked'); };
    }
  }, [anyOverlayOpen]);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 style={{ color: 'var(--text-primary)' }} className="text-xl sm:text-2xl font-bold">Settings</h1>
        <p style={{ color: 'var(--text-secondary)' }} className="mt-1 text-sm">
          System-wide configuration. Changes take effect immediately for all users.
        </p>
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibleCards.map(card => (
          <button
            key={card.id}
            onClick={() => setOpenModal(card.id)}
            style={{
              textAlign: 'left', cursor: 'pointer',
              background: 'var(--bg-card)',
              border: `1px solid ${isDark ? card.color.border : 'rgba(0,0,0,0.07)'}`,
              borderRadius: 14, padding: 22,
              transition: 'all 0.18s ease',
              boxShadow: 'none',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = isDark ? card.color.accentDark : card.color.accent;
              e.currentTarget.style.boxShadow = `0 4px 20px ${isDark ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.1)'}`;
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = isDark ? card.color.border : 'rgba(0,0,0,0.07)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {/* Icon box */}
            <div style={{
              width: 44, height: 44, borderRadius: 10, marginBottom: 14,
              background: isDark ? card.color.bg : `${card.color.accent}15`,
              border: `1px solid ${isDark ? card.color.border : `${card.color.accent}30`}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20,
            }}>
              {card.icon}
            </div>

            <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>
              {card.label}
            </p>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {card.description}
            </p>

            {/* Arrow */}
            <div style={{
              marginTop: 16, fontSize: 12, fontWeight: 600,
              color: isDark ? card.color.accentDark : card.color.accent,
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              Configure <span>→</span>
            </div>
          </button>
        ))}
      </div>

      {/* ── Modals ── */}
      <SettingsModal
        isOpen={openModal === 'candidate'}
        onClose={closeModal}
        title="Candidate Configuration"
        icon="👤"
      >
        <CandidateConfigContent isDark={isDark} onClose={closeModal} />
      </SettingsModal>

      <SettingsModal
        isOpen={openModal === 'email'}
        onClose={closeModal}
        title="Email Configuration"
        icon="✉️"
      >
        <EmailConfigContent isDark={isDark} onClose={closeModal} />
      </SettingsModal>

      <SettingsModal
        isOpen={openModal === 'impersonation'}
        onClose={closeModal}
        title="Impersonation"
        icon="🎭"
      >
        <ImpersonationConfigContent isDark={isDark} onClose={closeModal} />
      </SettingsModal>

      {isSuperAdmin() && (
        <SettingsModal
          isOpen={openModal === 'cache'}
          onClose={closeModal}
          title="Performance / Cache"
          icon="⚡"
        >
          <CacheConfigContent isDark={isDark} onClose={closeModal} />
        </SettingsModal>
      )}

      {isSuperAdmin() && (
        <SettingsModal
          isOpen={openModal === 'files'}
          onClose={closeModal}
          title="Downloads"
          icon="📄"
        >
          <FileConfigContent isDark={isDark} onClose={closeModal} />
        </SettingsModal>
      )}

      {isSuperAdmin() && (
        <SettingsModal
          isOpen={openModal === 'teams'}
          onClose={closeModal}
          title="Teams Meetings"
          icon="📅"
        >
          <TeamsConfigContent isDark={isDark} onClose={closeModal} />
        </SettingsModal>
      )}

      {isSuperAdmin() && (
        <SettingsModal
          isOpen={openModal === 'db-backups'}
          onClose={closeModal}
          title="Database Backups"
          icon="🗄️"
        >
          <DbBackupContent isDark={isDark} onClose={closeModal} />
        </SettingsModal>
      )}
    </div>
  );
}
